import type { TurnCredentials, SignalPayload } from "@riffbox/protocol";

export interface MeshCallbacks {
  sendSignal: (to: string, payload: SignalPayload) => void;
  /** A peer's combined audio+video stream is ready (or updated). */
  onRemoteStream: (peerId: string, stream: MediaStream) => void;
  onPeerGone: (peerId: string) => void;
}

interface Peer {
  pc: RTCPeerConnection;
  /** True if WE are the offerer for this pair (deterministic, avoids glare). */
  polite: boolean;
  remote: MediaStream;
}

/**
 * Full-mesh WebRTC voice (+ optional cam) for ≤4 people (PRD §4, non-goal: no
 * SFU). Signaling rides the ws-svc `signal` relay; media is forced through the
 * US coturn relay (`iceTransportPolicy: "relay"`) when TURN creds are present —
 * that relay is what fixes cross-border NAT traversal and gives a clean path.
 *
 * Glare is avoided by a deterministic initiator: the peer with the smaller
 * clientId makes the offer.
 */
export class VoiceMesh {
  private readonly peers = new Map<string, Peer>();
  private localStream: MediaStream | null = null;
  private turn: TurnCredentials | null = null;

  constructor(
    private readonly selfId: string,
    private readonly cb: MeshCallbacks,
  ) {}

  setTurn(turn: TurnCredentials): void {
    this.turn = turn;
  }

  /**
   * Apply freshly-minted TURN credentials to every live peer connection and
   * ICE-restart so the relay allocation re-authenticates BEFORE the old
   * credential's TTL elapses — otherwise relayed voice silently dies mid-movie.
   */
  async refreshTurn(turn: TurnCredentials): Promise<void> {
    this.turn = turn;
    for (const [peerId, peer] of this.peers) {
      try {
        peer.pc.setConfiguration(this.rtcConfig());
        // Only the deterministic initiator drives the renegotiation.
        if (this.selfId < peerId) {
          const offer = await peer.pc.createOffer({ iceRestart: true });
          await peer.pc.setLocalDescription(offer);
          this.cb.sendSignal(peerId, { kind: "offer", sdp: offer.sdp ?? "" });
        }
      } catch {
        /* transient; the next refresh tick will try again */
      }
    }
  }

  /** The local mic/cam stream. Tracks are added to every peer connection. */
  setLocalStream(stream: MediaStream | null): void {
    this.localStream = stream;
  }

  private rtcConfig(): RTCConfiguration {
    const hasTurn = !!this.turn && this.turn.urls.length > 0;
    const iceServers: RTCIceServer[] = hasTurn
      ? [{ urls: this.turn!.urls, username: this.turn!.username, credential: this.turn!.credential }]
      : [];
    // Use the best available path: direct when ICE can establish it, relay as a
    // fallback. coturn also answers STUN, so peers still get server-reflexive
    // candidates for direct attempts. (Relay-only is brittle — a lossy relay
    // leg has nowhere to fall back to.)
    return { iceServers, iceTransportPolicy: "all" };
  }

  /** Open a connection to a peer. The lower clientId initiates. */
  addPeer(peerId: string): void {
    if (peerId === this.selfId) return;
    // A re-announced peer (reconnect) must get a FRESH connection — never reuse
    // the stale one, or signaling lands on a dead PC and voice is lost.
    if (this.peers.has(peerId)) this.removePeer(peerId);
    const pc = new RTCPeerConnection(this.rtcConfig());
    const remote = new MediaStream();
    const initiator = this.selfId < peerId;
    const peer: Peer = { pc, polite: !initiator, remote };
    this.peers.set(peerId, peer);

    // Add whatever local tracks we have, then ensure BOTH media kinds are
    // negotiated. A host with no camera must still get a video m-line so it can
    // RECEIVE the guest's silhouette feed; otherwise, depending on who initiates,
    // the offer would omit video and the camera would never arrive.
    if (this.localStream) {
      for (const track of this.localStream.getTracks()) pc.addTrack(track, this.localStream);
    }
    if (!this.localStream?.getAudioTracks().length) pc.addTransceiver("audio", { direction: "recvonly" });
    if (!this.localStream?.getVideoTracks().length) pc.addTransceiver("video", { direction: "recvonly" });

    pc.addEventListener("track", (ev) => {
      remote.addTrack(ev.track);
      this.cb.onRemoteStream(peerId, remote);
    });

    pc.addEventListener("icecandidate", (ev) => {
      if (ev.candidate) {
        this.cb.sendSignal(peerId, {
          kind: "ice",
          candidate: {
            candidate: ev.candidate.candidate,
            sdpMid: ev.candidate.sdpMid,
            sdpMLineIndex: ev.candidate.sdpMLineIndex,
            usernameFragment: ev.candidate.usernameFragment,
          },
        });
      }
    });

    pc.addEventListener("connectionstatechange", () => {
      const st = pc.connectionState;
      if (st === "failed") {
        // A blip on the media path: try to self-heal with an ICE restart rather
        // than tearing the peer down (which would drop voice + silhouette).
        void this.renegotiate(peerId, true);
      } else if (st === "closed") {
        this.removePeer(peerId);
      }
    });

    if (initiator) {
      void this.makeOffer(peerId, peer);
    }
  }

  private async makeOffer(peerId: string, peer: Peer): Promise<void> {
    const offer = await peer.pc.createOffer();
    await peer.pc.setLocalDescription(offer);
    this.cb.sendSignal(peerId, { kind: "offer", sdp: offer.sdp ?? "" });
  }

  /** Re-offer to a peer (optionally with an ICE restart). Only the deterministic
   *  initiator drives it, to avoid glare. */
  private async renegotiate(peerId: string, iceRestart: boolean): Promise<void> {
    const peer = this.peers.get(peerId);
    if (!peer || this.selfId >= peerId) return;
    try {
      const offer = await peer.pc.createOffer({ iceRestart });
      await peer.pc.setLocalDescription(offer);
      this.cb.sendSignal(peerId, { kind: "offer", sdp: offer.sdp ?? "" });
    } catch {
      /* will get another chance on the next state change */
    }
  }

  async onSignal(from: string, payload: SignalPayload): Promise<void> {
    let peer = this.peers.get(from);
    if (!peer) {
      // An offer can arrive before our peer_joined bookkeeping; create lazily.
      this.addPeer(from);
      peer = this.peers.get(from);
      if (!peer) return;
    }
    const pc = peer.pc;

    if (payload.kind === "ice") {
      try {
        await pc.addIceCandidate(payload.candidate);
      } catch {
        /* candidate arrived before remote description; browsers buffer or drop */
      }
    } else if (payload.kind === "offer") {
      await pc.setRemoteDescription({ type: "offer", sdp: payload.sdp });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      this.cb.sendSignal(from, { kind: "answer", sdp: answer.sdp ?? "" });
    } else {
      await pc.setRemoteDescription({ type: "answer", sdp: payload.sdp });
    }
  }

  removePeer(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    try {
      peer.pc.close();
    } catch {
      /* already closed */
    }
    this.peers.delete(peerId);
    this.cb.onPeerGone(peerId);
  }

  setMicEnabled(on: boolean): void {
    this.localStream?.getAudioTracks().forEach((t) => (t.enabled = on));
  }

  setCamEnabled(on: boolean): void {
    this.localStream?.getVideoTracks().forEach((t) => (t.enabled = on));
  }

  /** Connection state of a given peer (for the e2e relay proxy). */
  peerConnection(peerId: string): RTCPeerConnection | undefined {
    return this.peers.get(peerId)?.pc;
  }

  peerIds(): string[] {
    return [...this.peers.keys()];
  }

  destroy(): void {
    for (const id of [...this.peers.keys()]) this.removePeer(id);
  }
}
